/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { 
    PanelBody, 
    TextControl, 
    TextareaControl,
    PanelColorSettings 
} from '@wordpress/components';
import { 
    InspectorControls, 
    useBlockProps,
    __experimentalColorGradientSettingsDropdown as ColorGradientSettingsDropdown,
    __experimentalUseMultipleOriginColorsAndGradients as useMultipleOriginColorsAndGradients,
} from '@wordpress/block-editor';

/**
 * Edit function for the urgent damage block
 */
export default function Edit({ attributes, setAttributes }) {
    const {
        title,
        description,
        cardTitle,
        cardSubtitle,
        cardContent,
        linkText,
        linkUrl,
        backgroundColor,
        textColor
    } = attributes;

    const blockProps = useBlockProps({
        className: 'urgent-damage-block-editor',
        style: {
            backgroundColor: backgroundColor || undefined,
            color: textColor || undefined,
        }
    });

    const colorSettings = useMultipleOriginColorsAndGradients();

    return (
        <>
            <InspectorControls>
                <PanelBody title={__('Content Settings', 'puroclean')} initialOpen={true}>
                    <TextControl
                        label={__('Title', 'puroclean')}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                        help={__('Main section title', 'puroclean')}
                    />
                    <TextareaControl
                        label={__('Description', 'puroclean')}
                        value={description}
                        onChange={(value) => setAttributes({ description: value })}
                        help={__('Main section description', 'puroclean')}
                        rows={4}
                    />
                </PanelBody>

                <PanelBody title={__('Emergency Card Settings', 'puroclean')} initialOpen={false}>
                    <TextControl
                        label={__('Card Title', 'puroclean')}
                        value={cardTitle}
                        onChange={(value) => setAttributes({ cardTitle: value })}
                        help={__('Emergency card title', 'puroclean')}
                    />
                    <TextControl
                        label={__('Card Subtitle', 'puroclean')}
                        value={cardSubtitle}
                        onChange={(value) => setAttributes({ cardSubtitle: value })}
                        help={__('Emergency card subtitle', 'puroclean')}
                    />
                    <TextControl
                        label={__('Card Content', 'puroclean')}
                        value={cardContent}
                        onChange={(value) => setAttributes({ cardContent: value })}
                        help={__('Emergency card content (e.g., phone number)', 'puroclean')}
                    />
                    <TextControl
                        label={__('Link Text', 'puroclean')}
                        value={linkText}
                        onChange={(value) => setAttributes({ linkText: value })}
                        help={__('Text for the action link', 'puroclean')}
                    />
                    <TextControl
                        label={__('Link URL', 'puroclean')}
                        value={linkUrl}
                        onChange={(value) => setAttributes({ linkUrl: value })}
                        help={__('URL for the action link', 'puroclean')}
                    />
                </PanelBody>

                <PanelBody title={__('Colors', 'puroclean')} initialOpen={false}>
                    <ColorGradientSettingsDropdown
                        __experimentalIsRenderedInSidebar
                        settings={[
                            {
                                colorValue: backgroundColor,
                                onColorChange: (value) => setAttributes({ backgroundColor: value }),
                                label: __('Background Color', 'puroclean'),
                            },
                            {
                                colorValue: textColor,
                                onColorChange: (value) => setAttributes({ textColor: value }),
                                label: __('Text Color', 'puroclean'),
                            },
                        ]}
                        panelId="urgent-damage-colors"
                        {...colorSettings}
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                <div className="py-8 border-2 border-dashed border-gray-300">
                    <div className="max-w-content mx-auto px-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                            
                            {/* Left Column - Main Content */}
                            <div className="space-y-4">
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {title || __('Add your title...', 'puroclean')}
                                </h2>
                                <p className="text-gray-600">
                                    {description || __('Add your description...', 'puroclean')}
                                </p>
                            </div>

                            {/* Right Column - Emergency Card Preview */}
                            <div className="relative">
                                {/* Triangle decoration indicator */}
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-700 opacity-50 rounded-br-lg"></div>
                                
                                {/* Main Card Preview */}
                                <div className="bg-red-600 text-white p-6 rounded-lg relative overflow-hidden">
                                    {/* Phone Icon Placeholder */}
                                    <div className="mb-4">
                                        <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                                            📞
                                        </div>
                                    </div>
                                    
                                    <h3 className="text-xl font-bold mb-2">
                                        {cardTitle || __('Card Title', 'puroclean')}
                                    </h3>
                                    <p className="text-sm mb-3 opacity-90">
                                        {cardSubtitle || __('Card Subtitle', 'puroclean')}
                                    </p>
                                    <p className="text-lg font-semibold mb-4">
                                        {cardContent || __('Card Content', 'puroclean')}
                                    </p>
                                    <div className="text-sm underline">
                                        {linkText || __('Link Text', 'puroclean')} →
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 text-center text-sm text-gray-500">
                            {__('✨ Urgent Damage Block - Desktop & Mobile Responsive', 'puroclean')}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
} 